class FrontisImageOptimizer {
  constructor(config = {}) {
    this.config = {
      lazyOffset: 50,
      imageQuality: 85,
      placeholder: {
        color: '#F1F1F1',
        blurHashLevel: 4
      },
      screens: {
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        '2xl': 1536
      },
      cdnUrl: config.cdnUrl || '',
      ...config
    };

    this.processedImages = new WeakSet();
    this.processedVideos = new WeakSet();
    this.init();
  }

  init() {
    // Priority loading for LCP elements
    this.processLCPElements();
    
    // Then handle other elements
    if (document.readyState === 'complete') {
      this.setupLazyLoading();
    } else {
      window.addEventListener('load', () => this.setupLazyLoading());
    }
  }

  // New method to handle LCP elements
  processLCPElements() {
    // Get potential LCP elements
    const lcpElements = this.identifyLCPElements();
    
    // Process them immediately with high priority
    lcpElements.forEach(element => {
      if (element.tagName === 'IMG') {
        this.processLCPImage(element);
      } else if (element.tagName === 'H1' || element.tagName === 'H2') {
        this.optimizeHeading(element);
      } else if (element.hasAttribute('data-frontis-bg')) {
        this.processLCPBackground(element);
      }
    });
  }

  // Identify potential LCP elements
  identifyLCPElements() {
    const elements = [];
    
    // First meaningful heading
    const mainHeading = document.querySelector('h1, h2');
    if (mainHeading) elements.push(mainHeading);

    // Hero image or first large image
    const heroImage = document.querySelector('.hero img, .banner img, header img');
    if (heroImage) elements.push(heroImage);

    // Large above-the-fold images
    const aboveFoldImages = Array.from(document.querySelectorAll('img')).filter(img => {
      const rect = img.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.width > 200 && rect.height > 200;
    });
    elements.push(...aboveFoldImages);

    return elements;
  }

  // Process LCP images with high priority
  async processLCPImage(img) {
    if (this.processedImages.has(img)) return;

    const originalSrc = img.getAttribute('src') || img.getAttribute('data-src');
    if (!originalSrc) return;

    try {
      // Preload the image
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = originalSrc;
      document.head.appendChild(preloadLink);

      const dimensions = await this.getImageSize(originalSrc);
      const optimizedSrc = this.getOptimizedUrl(originalSrc, {
        quality: 90, // Higher quality for LCP images
        width: dimensions.width
      });

      // Load immediately without placeholder
      img.src = optimizedSrc;
      img.setAttribute('width', dimensions.width);
      img.setAttribute('height', dimensions.height);
      img.style.aspectRatio = `${dimensions.width} / ${dimensions.height}`;
      
      // Add fetchpriority="high" for browsers that support it
      img.setAttribute('fetchpriority', 'high');
      
      this.processedImages.add(img);

    } catch (error) {
      console.warn('Frontis: Error processing LCP image:', error);
    }
  }

  // Optimize headings that might be LCP
  optimizeHeading(heading) {
    // Ensure text is visible immediately
    heading.style.display = 'block';
    
    // Add font-display: swap if using custom fonts
    const style = document.createElement('style');
    style.textContent = `
      ${heading.tagName.toLowerCase()} {
        font-display: swap !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Process LCP background images
  async processLCPBackground(element) {
    if (this.processedImages.has(element)) return;

    const bgUrl = element.getAttribute('data-frontis-bg') || this.getBgImageUrl(element);
    if (!bgUrl) return;

    try {
      // Preload the background image
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'image';
      preloadLink.href = bgUrl;
      document.head.appendChild(preloadLink);

      // Apply optimized background immediately
      const optimizedUrl = this.getOptimizedUrl(bgUrl, {
        quality: 90 // Higher quality for LCP elements
      });
      
      element.style.backgroundImage = `url("${optimizedUrl}")`;
      this.processedImages.add(element);

    } catch (error) {
      console.warn('Frontis: Error processing LCP background:', error);
    }
  }

  // Rest of the methods remain the same...
  setupLazyLoading() {
    this.setupObserver();
    this.processImages();
    this.processVideos();
    this.watchDOM();
  }

  // ... (include all other methods from previous implementation)
}

// Initialize with LCP optimization
export default FrontisImageOptimizer;

const frontisOptimizer = new FrontisImageOptimizer({
  lazyOffset: 100,
  placeholder: {
    color: '#E2E2E2',
    blurHashLevel: 4
  }
});